/* Bundle offer add-to-cart widget for the `bundel-product` template.
   The widget is a single toggle card:
   - off ("single") -> adds 1x the variant picked in the theme's own variant picker
   - on  ("bundle") -> adds the 2 chosen variants of this product (the "2 voor" bundle)
   Uses the theme AJAX cart (cart-store) and opens the cart drawer, exactly like <product-form>. */
import Cart from "cart-store";

class BundleOffer extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector("script.bundle-offer__data");
    if (!dataEl) return;

    this.data = JSON.parse(dataEl.textContent);
    this.variants = this.data.variants;
    this.options = this.data.options;

    this.mode = "single"; // "single" (card collapsed) | "bundle" (card expanded)
    this.stagedAction = this.querySelector("staged-action");
    this.addButton = this.querySelector(".bundle-offer__submit");
    this.errorEl = this.querySelector(".bundle-offer__error");
    this.bundleCard = this.querySelector('.bundle-offer__option[data-mode="bundle"]');
    this.toggle = this.querySelector(".bundle-offer__toggle");

    // Single mode uses the theme's own variant picker. We track its selection
    // through the product form's hidden id input (kept in sync by the theme).
    this.sectionId = this.dataset.sectionId;
    this.#bindThemeVariant();

    this.#bindOptionToggle();
    this.#bindRows();
    this.#bindSubmit();

    // Initial render of bundle rows + global state.
    this.#syncFirstRowToTheme();
    this.querySelectorAll(".bundle-offer__row").forEach((row) => this.#updateRow(row));
    this.#refreshState();
  }

  /* --- Single mode: follow the theme variant picker --------------------- */
  #bindThemeVariant() {
    const form =
      (this.sectionId && document.getElementById(`product-form-${this.sectionId}`)) ||
      document.querySelector('product-single form[id^="product-form-"]');
    this.variantInput = form ? form.querySelector('input[name="id"]') : null;

    if (this.variantInput) {
      // The theme dispatches a bubbling "change" on this input on every swap.
      this.variantInput.addEventListener("change", () => {
        // While the card is collapsed the first bundle row mirrors the customer's
        // pick above, so opening the card only asks them for the *second* item.
        if (this.mode === "single") this.#syncFirstRowToTheme();
        this.#refreshState();
      });
    }
  }

  #syncFirstRowToTheme() {
    const variant = this.#singleVariant();
    const row = this.querySelector('.bundle-offer__row[data-row="bundle-1"]');
    if (!variant || !row) return;

    row.querySelectorAll("select[data-option-position]").forEach((sel) => {
      const value = variant.options[Number(sel.dataset.optionPosition) - 1];
      // Colour values without a product image are not rendered as options.
      if (value != null && Array.from(sel.options).some((opt) => opt.value === value)) {
        sel.value = value;
      }
    });
    this.#updateRow(row);
  }

  #singleVariant() {
    const id = this.variantInput ? Number(this.variantInput.value) : null;
    return this.variants.find((v) => v.id === id) || this.variants.find((v) => v.available) || this.variants[0];
  }

  /* --- Variant resolution ----------------------------------------------- */
  #resolveVariant(row) {
    const selects = Array.from(row.querySelectorAll("select[data-option-position]")).sort(
      (a, b) => a.dataset.optionPosition - b.dataset.optionPosition
    );
    // Products with a single (default) variant render no selects.
    if (selects.length === 0) {
      return this.variants.find((v) => v.available) || this.variants[0];
    }
    const selected = selects.map((sel) => sel.value);
    return this.variants.find(
      (v) => v.options.length === selected.length && v.options.every((o, i) => o === selected[i])
    );
  }

  /* --- Offer toggle (collapsed single vs expanded bundle) ---------------- */
  #bindOptionToggle() {
    if (!this.toggle || !this.bundleCard) return;

    this.toggle.addEventListener("change", () => {
      this.mode = this.toggle.checked ? "bundle" : "single";
      this.bundleCard.classList.toggle("is-selected", this.toggle.checked);
      if (this.mode === "single") this.#syncFirstRowToTheme();
      this.#refreshState();
    });

    // Clicking the card header toggles the offer. The body is excluded so that
    // interacting with the variant rows can never collapse the card.
    const head = this.bundleCard.querySelector(".bundle-offer__option-head");
    if (head) {
      head.addEventListener("click", (e) => {
        if (e.target.closest("input")) return;
        this.toggle.checked = !this.toggle.checked;
        this.toggle.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
  }

  /* --- Variant rows ----------------------------------------------------- */
  #bindRows() {
    this.querySelectorAll(".bundle-offer__row select[data-option-position]").forEach((sel) => {
      sel.addEventListener("change", () => {
        this.#updateRow(sel.closest(".bundle-offer__row"));
        this.#refreshState();
      });
    });
  }

  #updateRow(row) {
    const variant = this.#resolveVariant(row);
    row._variant = variant;

    // Thumbnail
    const img = row.querySelector(".bundle-offer__thumb img");
    if (img && variant && variant.image) img.src = variant.image;

    this.#updateSwatches(row);
    this.#updateAvailabilityLabels(row);
  }

  // Show the colour dot matching the row's selected colour, hide the others.
  #updateSwatches(row) {
    row.querySelectorAll(".bundle-offer__select[data-has-swatch]").forEach((wrap) => {
      const select = wrap.querySelector("select[data-option-position]");
      if (!select) return;
      wrap.querySelectorAll(".bundle-offer__swatch").forEach((swatch) => {
        swatch.hidden = swatch.dataset.value !== select.value;
      });
    });
  }

  // Mark each option (per select) as sold out based on the OTHER selected
  // options in the same row, e.g. size "S" sold out for the chosen colour.
  #updateAvailabilityLabels(row) {
    const selects = Array.from(row.querySelectorAll("select[data-option-position]"));
    selects.forEach((sel) => {
      const position = Number(sel.dataset.optionPosition);
      Array.from(sel.options).forEach((opt) => {
        const available = this.#isComboAvailable(position, opt.value, selects);
        opt.textContent = available ? opt.value : `${opt.value} — ${this.data.soldOutSuffix}`;
        opt.classList.toggle("is-unavailable", !available);
      });
    });
  }

  // Is there an available variant matching `candidate` at `position`, while
  // honouring the current values of every OTHER select in the row?
  #isComboAvailable(position, candidate, selects) {
    return this.variants.some((v) => {
      if (!v.available) return false;
      if (v.options[position - 1] !== candidate) return false;
      return selects.every((sel) => {
        const p = Number(sel.dataset.optionPosition);
        return p === position || v.options[p - 1] === sel.value;
      });
    });
  }

  /* --- Items to add, availability, button state ------------------------- */
  #bundleRows() {
    return Array.from(this.querySelectorAll('.bundle-offer__row[data-row^="bundle"]'));
  }

  #items() {
    if (this.mode === "single") {
      const v = this.#singleVariant();
      return v ? [{ id: v.id, quantity: 1 }] : [];
    }
    return this.#bundleRows()
      .map((row) => row._variant)
      .filter(Boolean)
      .map((v) => ({ id: v.id, quantity: 1 }));
  }

  #allAvailable() {
    if (this.mode === "single") {
      const v = this.#singleVariant();
      return !!(v && v.available);
    }
    const rows = this.#bundleRows();
    return rows.length > 0 && rows.every((row) => row._variant && row._variant.available);
  }

  #refreshState() {
    const available = this.#allAvailable();
    const label = this.addButton.querySelector(".bundle-offer__submit-label");
    if (label) {
      label.textContent = available ? this.data.addLabel : this.data.soldOutLabel;
    }
    this.addButton.disabled = !available;
    this.addButton.classList.toggle("is-disabled", !available);
  }

  /* --- Add to cart ------------------------------------------------------ */
  #bindSubmit() {
    this.addButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.#submit();
    });
  }

  async #submit() {
    const items = this.#items();
    if (!items.length || !this.#allAvailable()) return;
    this.#clearError();
    try {
      if (this.stagedAction) await this.stagedAction.setState("DOING");
      await Cart.add(items);
      if (this.stagedAction) await this.stagedAction.setState("DONE");
      await Cart.resetVariantsBeingAdded();
      // Open the cart drawer / notification, mirroring product-form.js.
      const cart = document.querySelector(".modal--cart, .popup--cart-notification");
      if (cart && !cart.shown) {
        cart.open();
      } else if (cart && cart.interval) {
        cart.interval.reset();
        cart.interval.start();
      }
    } catch (error) {
      this.#showError(error?.message || "");
      if (this.stagedAction) await this.stagedAction.setState("IDLE");
    }
  }

  #showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  #clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = "";
      this.errorEl.hidden = true;
    }
  }
}

if (!customElements.get("bundle-offer")) {
  customElements.define("bundle-offer", BundleOffer);
}
